import {
  Activity,
  type Level,
  RUNS_ON_TEXT,
  type ServiceSignal,
  type ServiceStatus,
  type ServiceView,
  SIGNAL_KIND_TEXT,
  type SignalKind,
  type Watcher,
} from "@pagerless/schema";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ScreenHead } from "../components/ScreenHead";
import { ServicesMap, STATUS_TEXT, statusDot, statusLabel } from "../components/ServicesMap";
import { ErrorNote, Loading } from "../components/Shell";
import { useDeps, useResource } from "../context";
import { ago, localTime } from "../lib/format";
import { appPager, servicePager } from "../lib/pager";

/**
 * One app's services (/apps/:id): a list, failing first, and a map of where they run and what watches
 * them. Each service opens its activity (/apps/:id/services/:name): every signal it sent, newest
 * first, with all of its tags. Signal text is untrusted: it is only ever displayed.
 */

const enc = encodeURIComponent;

export const appHref = (projectId: string) => `/apps/${enc(projectId)}`;
export const serviceHref = (projectId: string, name: string) => `${appHref(projectId)}/services/${enc(name)}`;

const RANK: Record<ServiceStatus, number> = { failing: 0, silent: 1, ok: 2, none: 3 };

/** Failing first, then went quiet, OK, no signal yet; by name within each. */
export function sortServices(list: ServiceView[]): ServiceView[] {
  return [...list].sort((a, b) => RANK[a.status] - RANK[b.status] || a.name.localeCompare(b.name));
}

/** A service by its name or any name its signals arrive under ("job:nightly-backup"), ignoring case. */
export function findService(list: ServiceView[], name: string): ServiceView | undefined {
  const n = name.toLowerCase();
  return list.find((s) => s.name.toLowerCase() === n) ?? list.find((s) => s.keys.some((k) => k.toLowerCase() === n));
}

const count = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** "watched by 3 alarms" · "watched by 1 alarm and 1 rule" */
export function watchedText(watchers: Watcher[]): string {
  const alarms = watchers.filter((w) => w.kind === "alarm").length;
  const rules = watchers.length - alarms;
  const parts = [alarms > 0 ? count(alarms, "alarm") : "", rules > 0 ? count(rules, "rule") : ""].filter(Boolean);
  return parts.length === 0 ? "" : `watched by ${parts.join(" and ")}`;
}

/** "Last signal 3 min ago · 3:07 am · 1 problem open · watched by 3 alarms" */
export function serviceFacts(s: ServiceView, nowMs: number): string {
  return [
    s.lastSignalAt === null ? "" : `Last signal ${ago(s.lastSignalAt, nowMs)} · ${localTime(s.lastSignalAt, nowMs)}`,
    s.openProblems > 0 ? `${count(s.openProblems, "problem")} open` : "",
    watchedText(s.watchedBy),
  ]
    .filter(Boolean)
    .join(" · ");
}

export function AppServices() {
  const { now } = useDeps();
  const { id = "" } = useParams();
  const projects = useResource((a) => a.projects());
  const services = useResource((a) => a.services(id), id);

  if (projects.status === "loading" || services.status === "loading") return <Loading what="Loading your services" />;
  if (projects.status === "error") return <ErrorNote error={projects.error} retry={projects.reload} />;
  if (services.status === "error") return <ErrorNote error={services.error} retry={services.reload} />;
  const project = projects.data.find((p) => p.id === id);
  if (!project) return <ErrorNote error={new Error("We couldn't find this app.")} />;
  const list = sortServices(services.data.services);
  const { connectedAt } = services.data;
  const sub = [count(list.length, "service"), connectedAt === null ? "" : `connected ${ago(connectedAt, now())}`]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <title>{`${project.name} · Pagerless`}</title>
      <ScreenHead pager={appPager(list)} title={project.name} sub={sub}>
        <div className="btn-row">
          <Link to="/apps" className="btn alt sm">
            Back to apps
          </Link>
          <Link to={`${appHref(project.id)}/tools`} className="btn alt sm">
            Other tools
          </Link>
        </div>
      </ScreenHead>
      {list.length === 0 ? (
        <div className="box pad">
          <p style={{ margin: 0 }}>
            No services yet. Run <code>npx pagerless connect</code> in your code's folder, or send a first event.
          </p>
        </div>
      ) : (
        <>
          <h2 className="h3">Services</h2>
          <ul className="box list" aria-label="Services">
            {list.map((s) => (
              <li key={s.name} className="row" aria-label={s.name}>
                <span className={statusDot(s.status)} aria-hidden="true" />
                <div>
                  <b>
                    <Link to={serviceHref(project.id, s.name)}>{s.name}</Link>
                  </b>
                  <p className="muted">{s.runtime ?? RUNS_ON_TEXT[s.runsOn]}</p>
                  {serviceFacts(s, now()) && <p className="note svc-facts">{serviceFacts(s, now())}</p>}
                </div>
                <span className={statusLabel(s.status)}>{STATUS_TEXT[s.status]}</span>
              </li>
            ))}
          </ul>
          <h2 className="h3">Map</h2>
          <ServicesMap services={list} hrefOf={(s) => serviceHref(project.id, s.name)} />
        </>
      )}
    </>
  );
}

const LEVEL_TEXT: Record<Level, string> = { wake: "Asked to wake", tell: "Asked to tell", log: "Log only" };

const HOT_KINDS: ReadonlySet<SignalKind> = new Set(["error", "http_errors", "alarm", "task_stopped", "checkin_failed"]);
const OK_KINDS: ReadonlySet<SignalKind> = new Set(["alarm_ok", "resolved", "checkin", "task_running"]);

export function kindClass(k: SignalKind): string {
  return HOT_KINDS.has(k) ? "st hot" : OK_KINDS.has(k) ? "st ok" : "st";
}

/** Tag values longer than this are cut; the full text is on hover and one tap away. */
export const TAG_MAX = 48;

function Tag({ name, value }: { name: string; value: string }) {
  const [open, setOpen] = useState(false);
  const body = (shown: string) => (
    <>
      <span className="tag-k">{name}</span>
      <span className="tag-v">{shown}</span>
    </>
  );
  if (value.length <= TAG_MAX) return <li className="tag">{body(value)}</li>;
  return (
    <li className="tag">
      <button
        type="button"
        className="tag-btn"
        title={value}
        aria-expanded={open}
        aria-label={`${name}: ${open ? value : "show the full value"}`}
        onClick={() => setOpen((o) => !o)}
      >
        {body(open ? value : `${value.slice(0, TAG_MAX)}…`)}
      </button>
    </li>
  );
}

/** Everything known about a signal, as key/value chips: where it came from first, then its own tags. */
export function signalTags(sig: ServiceSignal, service: ServiceView): Array<[string, string]> {
  const own: Array<[string, string]> = [];
  if (sig.service !== service.name) own.push(["sent as", sig.service]);
  if (sig.env) own.push(["env", sig.env]);
  if (sig.level) own.push(["level", LEVEL_TEXT[sig.level]]);
  return [...own, ...Object.entries(sig.tags)];
}

function SignalRow({ sig, service, nowMs }: { sig: ServiceSignal; service: ServiceView; nowMs: number }) {
  const tags = signalTags(sig, service);
  return (
    <li className="row sig" aria-label={`${SIGNAL_KIND_TEXT[sig.kind]}: ${sig.title}`}>
      <time className="when" dateTime={new Date(sig.at).toISOString()} title={new Date(sig.at).toString()}>
        {localTime(sig.at, nowMs)}
      </time>
      <div>
        <p className="sig-head">
          <span className={kindClass(sig.kind)}>{SIGNAL_KIND_TEXT[sig.kind]}</span> <b>{sig.title}</b>
          {sig.count > 1 && <span className="muted"> · {sig.count.toLocaleString("en-US")} times</span>}
        </p>
        {tags.length > 0 && (
          <ul className="tags" aria-label="Tags">
            {tags.map(([k, v], i) => (
              // A tag may repeat a field's name ("env"); position keeps keys unique.
              // biome-ignore lint/suspicious/noArrayIndexKey: the list is fixed for this signal.
              <Tag key={`${i}:${k}`} name={k} value={v} />
            ))}
          </ul>
        )}
        {sig.problemId && (
          <p className="note">
            <Link to={`/problems/${enc(sig.problemId)}`}>Open the problem</Link>
          </p>
        )}
      </div>
    </li>
  );
}

export function ServiceActivity() {
  const { now } = useDeps();
  const { id = "", name = "" } = useParams();
  const [limit, setLimit] = useState<number>(Activity.readDefault);
  const projects = useResource((a) => a.projects());
  // A link may carry the name signals arrive under (a problem's service): find the service by it first.
  const activity = useResource(async (a) => {
    const all = await a.services(id);
    return a.serviceActivity(id, findService(all.services, name)?.name ?? name, limit);
  }, `${id}/${name}/${limit}`);

  if (projects.status === "loading" || activity.status === "loading") return <Loading what="Loading this service" />;
  if (projects.status === "error") return <ErrorNote error={projects.error} retry={projects.reload} />;
  if (activity.status === "error") return <ErrorNote error={activity.error} retry={activity.reload} />;
  const project = projects.data.find((p) => p.id === id);
  const appName = project?.name ?? "your app";
  const { service, signals } = activity.data;
  const sub = [service.runtime ?? RUNS_ON_TEXT[service.runsOn], watchedText(service.watchedBy)]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <title>{`${service.name} · ${appName} · Pagerless`}</title>
      <ScreenHead pager={servicePager(service, now())} title={service.name} sub={sub}>
        <Link to={appHref(id)} className="btn alt sm">
          Back to {appName}
        </Link>
      </ScreenHead>
      {(service.watchedBy.length > 0 || service.keys.length > 1) && (
        <ul className="tags svc-about" aria-label="About this service">
          {service.watchedBy.map((w) => (
            <Tag key={`${w.kind}:${w.name}`} name={w.kind === "alarm" ? "alarm" : "rule"} value={w.name} />
          ))}
          {service.keys.length > 1 && service.keys.map((k) => <Tag key={`key:${k}`} name="sent as" value={k} />)}
        </ul>
      )}
      <h2 className="h3">Signals</h2>
      {signals.length === 0 ? (
        <p className="muted">
          {service.status === "none"
            ? "Nothing received from this service yet. Signals show here as they arrive."
            : `Nothing received from this service in the last ${Activity.ttlDays} days.`}
        </p>
      ) : (
        <>
          <ul className="box list" aria-label="Signals">
            {signals.map((s) => (
              <SignalRow key={s.id} sig={s} service={service} nowMs={now()} />
            ))}
          </ul>
          {signals.length >= limit && limit < Activity.readMax && (
            <p>
              <button type="button" className="btn alt sm" onClick={() => setLimit(Activity.readMax)}>
                Show older signals
              </button>
            </p>
          )}
        </>
      )}
    </>
  );
}
